import ApartmentIcon from '@mui/icons-material/Apartment';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ImageNotSupportedOutlinedIcon from '@mui/icons-material/ImageNotSupportedOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Grid,
  Paper,
  Snackbar,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import type { AlertColor } from '@mui/material';
import strings from 'common/Translation/Translate';
import PageLoader from 'components/PageLoader';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Navigate, useNavigate } from 'react-router-dom';
import {
  getVenueConfigHealthProgressColor,
  VenueConfigurationHealthTooltipContent,
  type VenueConfigurationHealthItem,
} from 'screens/Authenticated/Configuration/VenueConfiguration/VenueConfigurationHealthPanel';
import axiosInstance from 'utils/axios';
import Config from 'utils/Config';
import { routes } from 'utils/constants/routes';
import { URLS } from 'utils/constants/urls';
import { parentOrgInitData, User } from 'utils/redux/reducer/authentication-slice';

type CountItem = {
  count: number;
  complete: boolean;
};

type TemplateStatus = {
  contractTemplate: boolean;
  proposalTemplate: boolean;
  piTemplate: boolean;
  beoTemplate: boolean;
  invoiceTemplate: boolean;
};

type MissingConfigItem = {
  venueId: number;
  venueName: string;
  eventTypeId: number;
  eventTypeName: string;
};

type VenueEventStatus = {
  eventTypeId: number;
  eventTypeName: string;
  configured: boolean;
};

type ConfiguredConfigItem = {
  venueConfigurationId: number;
  eventTypeId: number;
  eventTypeName: string;
  completionPercentage: number;
  missingFields?: string[];
  configurationHealth?: VenueConfigurationHealthItem[];
};

type VenueEventsItem = {
  venueId: number;
  venueName: string;
  imageUrl?: string | null;
  hasImage: boolean;
  configuredConfigs: ConfiguredConfigItem[];
  events: VenueEventStatus[];
};

type ReadinessDashboardResponse = {
  propertyName: string;
  organizationId: number;
  venues: CountItem;
  pricePackages: CountItem;
  venueConfigurations: CountItem;
  addOns: CountItem;
  templates: TemplateStatus;
  bookingReady: boolean;
  missingConfigurations: MissingConfigItem[];
  venueEvents: VenueEventsItem[];
};

type ReadinessRow = {
  key: string;
  label: string;
  item: CountItem;
};

const StatusIcon: FC<{ complete: boolean }> = ({ complete }) =>
  complete ? (
    <CheckCircleOutlineIcon color="success" fontSize="small" />
  ) : (
    <CancelOutlinedIcon color="error" fontSize="small" />
  );

const healthTooltipSlotProps = {
  tooltip: {
    sx: {
      bgcolor: 'background.paper',
      color: 'text.primary',
      boxShadow: 3,
      border: 1,
      borderColor: 'divider',
      maxWidth: 300,
      p: 1.5,
    },
  },
  arrow: {
    sx: {
      color: 'background.paper',
      '&::before': {
        border: 1,
        borderColor: 'divider',
      },
    },
  },
};

const ConfiguredConfigCompactCard: FC<{ config: ConfiguredConfigItem }> = ({ config }) => {
  const rounded = Math.round(config.completionPercentage ?? 0);
  const progressColor = getVenueConfigHealthProgressColor(rounded);

  return (
    <Tooltip
      arrow
      placement="top"
      slotProps={healthTooltipSlotProps}
      title={
        <VenueConfigurationHealthTooltipContent
          value={config.completionPercentage ?? 0}
          configurationHealth={config.configurationHealth}
          missingFields={config.missingFields}
        />
      }>
      <Box
        sx={{
          px: 0.8,
          py: 0.45,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.6,
          border: 1,
          borderColor: 'success.light',
          borderRadius: 1,
          bgcolor: '#f2fbf5',
          cursor: 'default',
          minWidth: 0,
        }}>
        <Box sx={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}>
          <CircularProgress variant="determinate" value={rounded} size={22} thickness={4} color={progressColor} />
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
            <Typography variant="caption" sx={{ fontSize: '0.5rem', fontWeight: 800 }}>
              {rounded}%
            </Typography>
          </Box>
        </Box>
        <Typography variant="caption" color="text.primary" fontWeight={600} noWrap sx={{ maxWidth: 130 }}>
          {config.eventTypeName}
        </Typography>
      </Box>
    </Tooltip>
  );
};

const VenueImagePreview: FC<{ venue: VenueEventsItem }> = ({ venue }) => {
  const imageSrc = venue.hasImage && venue.imageUrl ? `${Config.BaseURL}${venue.imageUrl}` : null;

  return (
    <Box
      sx={{
        width: '100%',
        height: 88,
        flexShrink: 0,
        borderRadius: '8px 8px 0 0',
        overflow: 'hidden',
        bgcolor: '#eef3f9',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
      {imageSrc ? (
        <Box
          component="img"
          src={imageSrc}
          alt={venue.venueName}
          sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      ) : (
        <Stack alignItems="center" spacing={0.25} sx={{ px: 0.5, textAlign: 'center' }}>
          <ImageNotSupportedOutlinedIcon color="disabled" fontSize="small" />
          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
            {strings.property_readiness_venue_image_not_uploaded}
          </Typography>
        </Stack>
      )}
    </Box>
  );
};

const PropertyReadinessDashboard: FC = () => {
  const navigate = useNavigate();
  const user = useSelector(User) as { role?: { value?: string } } | null;
  const { title } = useSelector(parentOrgInitData) as { title?: string };
  const isSuperAdmin = user?.role?.value === 'ROLE_BBR_ADMIN';

  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState<ReadinessDashboardResponse | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: AlertColor }>({
    open: false,
    message: '',
    severity: 'info',
  });

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get<ReadinessDashboardResponse>(URLS.propertyReadinessDashboard);
      setDashboard(data);
    } catch {
      setSnackbar({
        open: true,
        message: strings.property_readiness_load_error,
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isSuperAdmin) {
      fetchDashboard();
    }
  }, [fetchDashboard, isSuperAdmin]);

  const handleCreateMissingConfigs = () => {
    if (!dashboard?.missingConfigurations.length) return;

    const preselectedVenueIds = [...new Set(dashboard.missingConfigurations.map(item => item.venueId))];
    const preselectedEventTypeIds = [...new Set(dashboard.missingConfigurations.map(item => item.eventTypeId))];

    navigate(routes.bulkCreateVenueConfiguration, {
      state: { preselectedVenueIds, preselectedEventTypeIds },
    });
  };

  if (!isSuperAdmin) {
    return <Navigate to={routes.dashboard} replace />;
  }

  const propertyName = dashboard?.propertyName ?? title ?? '—';
  const missingCount = dashboard?.missingConfigurations.length ?? 0;

  const readinessRows: ReadinessRow[] = dashboard
    ? [
        { key: 'venues', label: strings.property_readiness_venue_setup, item: dashboard.venues },
        { key: 'packages', label: strings.property_readiness_price_packages, item: dashboard.pricePackages },
        {
          key: 'configs',
          label: strings.property_readiness_venue_configurations,
          item: dashboard.venueConfigurations,
        },
        { key: 'addons', label: strings.property_readiness_addons, item: dashboard.addOns },
      ]
    : [];

  const templateRows = dashboard
    ? [
        {
          key: 'contract',
          label: strings.property_readiness_template_contract,
          uploaded: dashboard.templates.contractTemplate,
        },
        {
          key: 'proposal',
          label: strings.property_readiness_template_proposal,
          uploaded: dashboard.templates.proposalTemplate,
        },
        { key: 'pi', label: strings.property_readiness_template_pi, uploaded: dashboard.templates.piTemplate },
        { key: 'beo', label: strings.property_readiness_template_beo, uploaded: dashboard.templates.beoTemplate },
        {
          key: 'invoice',
          label: strings.property_readiness_template_invoice,
          uploaded: dashboard.templates.invoiceTemplate,
        },
      ]
    : [];

  const venueCards = dashboard?.venueEvents ?? [];
  const totalReadinessItems = readinessRows.length + templateRows.length;
  const completedReadinessItems =
    readinessRows.filter(row => row.item.complete).length + templateRows.filter(row => row.uploaded).length;
  const readinessPercent = totalReadinessItems ? Math.round((completedReadinessItems / totalReadinessItems) * 100) : 0;

  return (
    <>
      {loading && <PageLoader />}
      <Box sx={{ minHeight: '100%', bgcolor: '#f6f8fc', py: 3 }}>
        <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3 } }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', md: 'center' }}
            spacing={2.5}
            sx={{ mb: 2.5 }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Box
                sx={{
                  width: 60,
                  height: 60,
                  borderRadius: 2,
                  bgcolor: '#ffffff',
                  border: '1px solid #d8e2f1',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'primary.main',
                  boxShadow: '0 10px 24px rgba(15, 23, 42, 0.06)',
                }}>
                <ApartmentIcon sx={{ fontSize: 36 }} />
              </Box>
              <Box>
                <Typography variant="h5" fontWeight={800} color="#10234d">
                  {strings.property_readiness_onboarding_heading.replace('{property}', propertyName)}
                </Typography>
                <Typography variant="body2" color="#61708f" sx={{ mt: 0.4 }}>
                  {strings.property_readiness_onboarding_subtitle}
                </Typography>
              </Box>
            </Stack>

            {!loading && (
              <Alert
                severity={dashboard?.bookingReady ? 'success' : 'warning'}
                icon={dashboard?.bookingReady ? <CheckCircleOutlineIcon /> : <WarningAmberIcon />}
                sx={{
                  minWidth: { xs: '100%', md: 380 },
                  border: '1px solid',
                  borderColor: dashboard?.bookingReady ? '#bfe8cb' : '#f5dca6',
                  bgcolor: dashboard?.bookingReady ? '#f1fbf4' : '#fff8ea',
                  color: '#10234d',
                  '& .MuiAlert-icon': { color: dashboard?.bookingReady ? '#2f9e44' : '#f0a31a' },
                }}>
                <Typography variant="body2" fontWeight={800}>
                  {dashboard?.bookingReady
                    ? strings.property_readiness_booking_ready_short
                    : strings.property_readiness_booking_not_ready_short}
                </Typography>
                <Typography variant="body2" color="#7a5c1c">
                  {dashboard?.bookingReady
                    ? strings.property_readiness_booking_ready_detail
                    : strings.property_readiness_booking_not_ready_detail}
                </Typography>
              </Alert>
            )}
          </Stack>

          <Grid container spacing={2.25} alignItems="stretch" sx={{ mb: 2.25 }}>
            <Grid item xs={12} lg={6}>
              <Paper
                variant="outlined"
                sx={{
                  height: '100%',
                  borderRadius: 2,
                  borderColor: '#dfe7f3',
                  boxShadow: '0 10px 26px rgba(15, 23, 42, 0.05)',
                  overflow: 'hidden',
                }}>
                <Box sx={{ p: 2, pb: 1 }}>
                  <Typography variant="subtitle1" fontWeight={800} color="#10234d">
                    {strings.property_readiness_onboarding_progress}
                  </Typography>
                </Box>
                <Grid container spacing={1} sx={{ px: 2, pb: 2 }} alignItems="center">
                  <Grid item xs={12}>
                    <Stack spacing={1.25}>
                      {readinessRows.map(row => (
                        <Stack key={row.key} direction="row" alignItems="center" spacing={1.25}>
                          <StatusIcon complete={row.item.complete} />
                          <Typography variant="body2" fontWeight={700} color="#26385f" sx={{ flex: 1 }}>
                            {row.label}
                          </Typography>
                          <Typography variant="body2" fontWeight={800} color="#26385f">
                            {row.item.count}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>
                  </Grid>
                </Grid>
                <Box sx={{ borderTop: '1px solid #e6edf6', px: 2, py: 1.1 }}>
                  <Button
                    size="small"
                    endIcon={<ArrowForwardIcon />}
                    onClick={() => navigate(routes.propertyOnboarding)}
                    sx={{ textTransform: 'none', fontWeight: 800 }}>
                    {strings.property_readiness_view_setup_guide}
                  </Button>
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} lg={6}>
              <Paper
                variant="outlined"
                sx={{
                  height: '100%',
                  borderRadius: 2,
                  borderColor: '#dfe7f3',
                  boxShadow: '0 10px 26px rgba(15, 23, 42, 0.05)',
                  overflow: 'hidden',
                }}>
                <Box sx={{ p: 2, pb: 1 }}>
                  <Typography variant="subtitle1" fontWeight={800} color="#10234d">
                    {strings.property_readiness_section_templates}
                  </Typography>
                </Box>
                <Stack spacing={1.25} sx={{ px: 2, pb: 2 }}>
                  {templateRows.map(row => (
                    <Stack key={row.key} direction="row" justifyContent="space-between" alignItems="center">
                      <Typography
                        display="flex"
                        alignItems="center"
                        gap={0.7}
                        variant="body2"
                        fontWeight={700}
                        color="#26385f">
                        {row.uploaded ? (
                          <CheckCircleOutlineIcon color="success" sx={{ fontSize: 18 }} />
                        ) : (
                          <CancelOutlinedIcon color="error" sx={{ fontSize: 18 }} />
                        )}
                        {row.label}
                      </Typography>
                      <Stack direction="row" spacing={0.7} alignItems="center">
                        <Typography variant="body2" color="#506080" fontWeight={700}>
                          {row.uploaded
                            ? strings.property_readiness_template_uploaded
                            : strings.property_readiness_template_missing}
                        </Typography>
                      </Stack>
                    </Stack>
                  ))}
                </Stack>
              </Paper>
            </Grid>
          </Grid>

          <Paper
            variant="outlined"
            sx={{
              borderRadius: 2,
              borderColor: '#dfe7f3',
              boxShadow: '0 10px 26px rgba(15, 23, 42, 0.05)',
              overflow: 'hidden',
            }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              spacing={1}
              sx={{ px: 2, py: 1.75 }}>
              <Box>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="subtitle1" fontWeight={900} color="#10234d">
                    {strings.property_readiness_section_missing_configs}
                  </Typography>
                  {!loading && missingCount > 0 && (
                    <Chip
                      size="small"
                      label={missingCount}
                      color="error"
                      sx={{ height: 20, fontWeight: 800, bgcolor: '#ffe8ec', color: '#e04f5f' }}
                    />
                  )}
                </Stack>
                <Typography variant="body2" color="#61708f">
                  {loading
                    ? '—'
                    : missingCount === 0
                    ? strings.property_readiness_no_missing_configs
                    : strings.property_readiness_missing_configs_hint}
                </Typography>
              </Box>
              {!loading && missingCount > 0 && (
                <Button
                  variant="contained"
                  onClick={handleCreateMissingConfigs}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 800,
                    bgcolor: '#0b3b8f',
                    borderRadius: 1,
                    px: 2,
                    '&:hover': { bgcolor: '#082f73' },
                  }}>
                  {strings.property_readiness_create_missing_configs}
                </Button>
              )}
            </Stack>

            <Box sx={{ px: 2, pb: 2 }}>
              {loading ? (
                <Typography variant="body2" color="text.secondary">
                  —
                </Typography>
              ) : venueCards.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {strings.property_readiness_no_missing_configs}
                </Typography>
              ) : (
                <Grid container spacing={2}>
                  {venueCards.map(venue => {
                    const missingEvents = venue.events.filter(event => !event.configured);
                    const configuredConfigs = venue.configuredConfigs ?? [];

                    return (
                      <Grid item xs={12} md={6} xl={4} key={venue.venueId}>
                        <Paper
                          variant="outlined"
                          sx={{
                            height: '100%',
                            borderColor: '#dfe7f3',
                            borderRadius: 2,
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column',
                            bgcolor: '#fff',
                          }}>
                          <VenueImagePreview venue={venue} />
                          <Box sx={{ p: 1.5, flex: 1 }}>
                            <Typography
                              variant="subtitle2"
                              fontWeight={900}
                              color="#10234d"
                              noWrap
                              title={venue.venueName}>
                              {venue.venueName}
                            </Typography>

                            {configuredConfigs.length > 0 && (
                              <Box sx={{ mt: 1.4 }}>
                                <Typography
                                  variant="caption"
                                  color="#61708f"
                                  fontWeight={700}
                                  display="block"
                                  sx={{ mb: 0.75 }}>
                                  {strings.property_readiness_events_configured}
                                </Typography>
                                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                                  {configuredConfigs.map(config => (
                                    <ConfiguredConfigCompactCard key={config.eventTypeId} config={config} />
                                  ))}
                                </Stack>
                              </Box>
                            )}

                            {missingEvents.length > 0 && (
                              <Box sx={{ mt: 1.4 }}>
                                <Typography
                                  variant="caption"
                                  color="#61708f"
                                  fontWeight={700}
                                  display="block"
                                  sx={{ mb: 0.75 }}>
                                  {strings.property_readiness_events_missing}
                                </Typography>
                                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                                  {missingEvents.map(event => (
                                    <Chip
                                      key={event.eventTypeId}
                                      size="small"
                                      icon={<CancelOutlinedIcon />}
                                      label={event.eventTypeName}
                                      variant="outlined"
                                      sx={{
                                        height: 24,
                                        borderColor: '#ff9aa6',
                                        color: '#e04f5f',
                                        bgcolor: '#fff7f8',
                                        '& .MuiChip-icon': { color: '#e04f5f', fontSize: 15 },
                                        '& .MuiChip-label': { px: 0.75, fontSize: '0.72rem', fontWeight: 700 },
                                      }}
                                    />
                                  ))}
                                </Stack>
                              </Box>
                            )}
                          </Box>
                          <Box sx={{ borderTop: '1px solid #edf1f7', px: 1.5, py: 1 }}>
                            <Button
                              size="small"
                              endIcon={<ArrowForwardIcon />}
                              onClick={() => navigate(routes.venueConfiguration)}
                              sx={{ textTransform: 'none', fontWeight: 800 }}>
                              {strings.property_readiness_view_details}
                            </Button>
                          </Box>
                        </Paper>
                      </Grid>
                    );
                  })}
                </Grid>
              )}

              {!loading && venueCards.length > 0 && (
                <Alert
                  icon={<InfoOutlinedIcon />}
                  severity="info"
                  sx={{
                    mt: 2,
                    py: 0.4,
                    border: '1px solid #cfe2ff',
                    bgcolor: '#f4f9ff',
                    '& .MuiAlert-message': { width: '100%' },
                  }}
                  action={
                    <Button
                      size="small"
                      endIcon={<OpenInNewIcon />}
                      onClick={() => navigate(routes.bulkCreateVenueConfiguration)}
                      sx={{ textTransform: 'none', fontWeight: 800 }}>
                      {strings.property_readiness_learn_how}
                    </Button>
                  }>
                  <Typography variant="body2" color="#12376f">
                    <strong>{strings.property_readiness_tip_label}</strong> {strings.property_readiness_bulk_tip}
                  </Typography>
                </Alert>
              )}
            </Box>
          </Paper>
        </Container>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}
          sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default PropertyReadinessDashboard;
